import { useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Badge,
  Box,
  Card,
  Flex,
  InputGroup,
  Stack,
  Textarea,
  Text,
  useToast,
} from '@chakra-ui/react';
import { CirlceIcon, allIcons } from '../utils/Icon';
import UploadComponent from '../import/UploadComponent';
import { TState } from '../../redux/store';
import { downloadDocuments } from '../../api/tabsApi';
import { downloadFile } from '../../helpers';
import { useCanI } from '../../redux/actions';

const Group = ({ d, onUpdate }: { d: any; onUpdate: any }) => {
  const filesComms = useSelector((state: TState) => state.filesComms);
  const rowData = useSelector((state: TState) => state.rowData);
  const toast = useToast();

  const ref = useRef<any>();
  const startValue = filesComms[d._id];

  return (
    <InputGroup w='100%' flexDirection='column'>
      <Text>Commentaire</Text>
      <Textarea
        minH='50px'
        ref={ref}
        defaultValue={filesComms[d._id]}
        onBlur={async () => {
          if (ref.current.value !== startValue) {
            await onUpdate({
              _id: rowData._id,
              [`${d._id}-comment`]: ref.current.value,
            });

            toast({
              title: 'Commentaire mis à jour',
              status: 'success',
            });
          }
        }}
        bgColor='white'
      />
    </InputGroup>
  );
};

const DocumentCard = ({
  d,
  onUpload,
  groupFiles,
  onView,
  onUpdate,
}: {
  d: any;
  onUpload: any;
  groupFiles: any;
  onView: any;
  onUpdate: any;
}) => {
  const filesStatus = useSelector((state: TState) => state.filesStatus);
  const rowData = useSelector((state: TState) => state.rowData);
  const theme = useSelector((state: TState) => state.theme);

  const canI = useCanI();

  const canComment = canI(d._id, 'comment');
  const canValidate = canI(d._id, 'validate');
  const canAdd = canI(d._id, 'add');
  const canRead = canI(d._id, 'read') || canI(d._id, 'delete');
  const canDownload = canI(d._id, 'download');

  const handleDownload = async (docId: string, name: string) => {
    const respData = await downloadDocuments(docId, rowData._id);
    await downloadFile(respData, `${name}.zip`);
  };

  return (
    <Card p={4} flex={1} bgColor='#f9fafb'>
      <Flex justify='space-between' direction={['column', 'row']}>
        {/* Infos */}
        <Flex w='100%'>
          <Box>
            <Text fontWeight='bold' fontSize='lg'>
              {d.name}
            </Text>

            <Text fontSize='sm' color='gray.500'>
              {d.description}
            </Text>

            {canValidate && (
              <>
                <Badge
                  mt={2}
                  colorScheme={filesStatus[d._id] ? 'green' : 'gray'}
                  onClick={() => {
                    onUpdate({
                      _id: rowData._id,
                      [`${d._id}-isDone`]: !filesStatus[d._id],
                    });
                  }}
                  boxShadow='md'
                  _hover={{
                    boxShadow: 'xs',
                  }}
                  cursor='pointer'
                  transition='all ease .1s'
                >
                  {filesStatus[d._id] ? 'Complet' : 'Incomplet'}
                </Badge>
              </>
            )}
          </Box>
          <Box flexGrow={1} />
        </Flex>

        {/* Actions */}
        <Flex w='100%' my={[-2, 0]}>
          <Box flexGrow={1} />
          <Stack direction='row' spacing={1}>
            {canRead && groupFiles[d._id]?.length ? (
              <CirlceIcon
                icon={<allIcons.HiEye />}
                onClick={() => onView(d._id)}
                label={`${groupFiles[d._id]?.length}`}
                bgColor={theme.dark}
                color={theme.none}
                maxHeight='34px'
              />
            ) : null}

            {canAdd && (
              <UploadComponent
                multiple
                onUpload={(file) => onUpload(d._id, file)}
                bgColor={theme.dark}
                color={theme.none}
              />
            )}

            {canDownload && groupFiles[d._id]?.length && (
              <CirlceIcon
                as={allIcons.HiMiniArrowDownOnSquareStack}
                onClick={() => handleDownload(d._id, d.name)}
                bgColor={theme.dark}
                color={theme.none}
              />
            )}
          </Stack>
        </Flex>
      </Flex>
      <Flex mt={[0, 3]}>
        {canComment && <Group d={d} onUpdate={onUpdate} />}
      </Flex>
    </Card>
  );
};

const DocumentTable = (props: any) => {
  const canI = useCanI();

  const filteredData = props.data.filter((d: any) => {
    const canComment = canI(d._id, 'comment');
    const canValidate = canI(d._id, 'validate');
    const canAdd = canI(d._id, 'add');
    const canRead = canI(d._id, 'read') || canI(d._id, 'delete');
    const canDownload = canI(d._id, 'download');

    return canComment || canValidate || canAdd || canRead || canDownload;
  });

  return (
    <Flex gap={3} direction='column'>
      {filteredData.map((d: any) => (
        <DocumentCard d={d} {...props} />
      ))}
    </Flex>
  );
};

export default DocumentTable;
